import React, { FunctionComponent, useEffect, useRef } from 'react';
import { useIntercom } from 'react-use-intercom';
import { connect } from 'react-redux';
import { RootState } from 'store/index';
import { user_userInfo } from 'store/user/selectors';
import { AccountInfoParsed } from 'services/generatedClientFromSwagger';
import moment from 'moment';
import { makeStyles } from '@material-ui/core';
import config, { IntercomPlacement } from 'config/common';
import analyticsManager from 'helpers/analyticsManager';
import { ReviewedMixpanelEvents } from 'helpers/mixpanelReviewedEvents';

const useMobileIntercomStyles = makeStyles((theme) => ({
    '@global': {
        [theme.breakpoints.down('lg')]: {
            '.intercom-lightweight-app-launcher, .intercom-launcher-frame': {
                bottom: '72px !important',
                right: '15px !important',
            },
            // :not(.intercom-messenger-frame) — the chat window itself must keep Intercom's own
            // full-screen mobile layout; offsetting it leaves a gap at the viewport edge (RND-86).
            '.intercom-namespace div:not(.intercom-messenger-frame)': {
                bottom: '72px !important',
                right: '15px !important',
            },
        },
    },
}));

export const INTERCOM_LIVE_CHAT_LAUNCHER_ID = 'intercom-live-chat-launcher';

type IntercomChatProps = {
    userInfo: AccountInfoParsed;
};

const IntercomChat: FunctionComponent<IntercomChatProps> = ({ userInfo }) => {
    const { boot, shutdown, update, isOpen, trackEvent } = useIntercom();
    const isNavBar = config.intercomPlacement === IntercomPlacement.navBar;
    useMobileIntercomStyles();
    const openTimestampRef = useRef<number | null>(null);
    const prevIsOpenRef = useRef(false);

    useEffect(() => {
        if (isOpen && !prevIsOpenRef.current) {
            openTimestampRef.current = Date.now();
            analyticsManager.trackEvent(ReviewedMixpanelEvents.LIVE_CHAT_OPENED, {
                current_page_url: window.location.href,
                intercom_placement: config.intercomPlacement,
            });
        } else if (!isOpen && prevIsOpenRef.current) {
            const duration = openTimestampRef.current
                ? Math.round((Date.now() - openTimestampRef.current) / 1000)
                : undefined;
            openTimestampRef.current = null;
            analyticsManager.trackEvent(ReviewedMixpanelEvents.LIVE_CHAT_CLOSED, {
                current_page_url: window.location.href,
                intercom_placement: config.intercomPlacement,
                session_duration_seconds: duration,
            });
        }
        prevIsOpenRef.current = isOpen;
    }, [isOpen]);

    const ftdExists = userInfo?.cz_extension_sumAllDeposits && userInfo?.cz_extension_sumAllDeposits > 0 ? 'Yes' : 'No';

    useEffect(() => {
        const formattedCreatedAt = userInfo.custom_field_createdon
            ? moment(userInfo.custom_field_createdon).format('MMM DD, YYYY HH:mm')
            : undefined;

        try {
            boot({
                userId: userInfo.id ?? '',
                name: userInfo.lastName ?? '',
                email: userInfo.email ?? '',
                createdAt: formattedCreatedAt,
                customAttributes: {
                    ftd_exists: ftdExists,
                    'FTD Exists': ftdExists,
                },
                ...(isNavBar && {
                    hideDefaultLauncher: true,
                    customLauncherSelector: `#${INTERCOM_LIVE_CHAT_LAUNCHER_ID}`,
                }),
            });

            trackEvent('onShow', {
                email: userInfo.email ?? '',
                userId: userInfo.id,
                name: userInfo.firstName ?? '',
                createdAt: formattedCreatedAt,
                ftd_exists: ftdExists,
            });

            update({
                email: userInfo?.email ?? '',
                userId: userInfo?.id,
                customAttributes: {
                    ftd_exists: ftdExists,
                },
            });
        } catch (error) {
            analyticsManager.trackEvent(ReviewedMixpanelEvents.INTERCOM_LOAD_FAILED, {
                current_page_url: window.location.href,
                intercom_placement: config.intercomPlacement,
                error_message: error instanceof Error ? error.message : String(error),
            });
        }

        return () => {
            shutdown();
        };
    }, [
        boot,
        shutdown,
        trackEvent,
        userInfo.email,
        userInfo.id,
        update,
        userInfo.custom_field_createdon,
        userInfo.lastName,
        userInfo.firstName,
        userInfo.custom_field_sumofdeposits,
        ftdExists,
        isNavBar,
    ]);

    return <></>;
};

const mapStateToProps = (state: RootState) => ({
    userInfo: user_userInfo(state),
});

const mapDispatchToProps = {};

export default connect(mapStateToProps, mapDispatchToProps)(React.memo(IntercomChat));
